import { useState } from "react";
import { CalendarDays, Download } from "lucide-react";
import type { CalendarException, Holiday, OrganizationCalendar } from "@opus-dt/shared-types";
import { groupExceptionsByYear, mergeHolidays } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { Notify, toast } from "@/components/ui/notify";
import { ExceptionsList } from "@/features/calendar/ExceptionsList";
import { HolidayReview } from "./HolidayReview";
import { useHolidays, useLoadHolidays, useUpdateOrganizationCalendar } from "./hooks";

interface Props {
  calendar: OrganizationCalendar | null;
  canEdit: boolean;
}

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR, CURRENT_YEAR + 1, CURRENT_YEAR + 2];

// Feriados e paradas gerais da fábrica.
// Duas entradas para a mesma lista: o CATÁLOGO nacional (revisável, uma vez por ano) e o
// lançamento à mão, pelo MESMO `<ExceptionsList>` da linha. O que o catálogo grava são
// exceções datadas comuns — indistinguíveis de uma lançada à mão, e editáveis uma a uma.
export function HolidaysSection({ calendar, canEdit }: Props) {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [review, setReview] = useState<Holiday[] | null>(null);
  const holidaysQuery = useHolidays(year, review !== null);
  const loadHolidays = useLoadHolidays();
  const update = useUpdateOrganizationCalendar();

  const exceptions = calendar?.exceptions ?? [];
  const groups = groupExceptionsByYear(exceptions);
  const busy = loadHolidays.isPending || update.isPending;

  async function saveExceptions(next: CalendarException[]) {
    await update.mutateAsync({
      weeklyShifts: calendar?.weeklyShifts ?? [],
      exceptions: next,
      recurringExceptions: calendar?.recurringExceptions ?? [],
    });
  }

  async function confirmLoad(chosen: Holiday[]) {
    const { added } = mergeHolidays(exceptions, chosen);
    if (added.length === 0) {
      // Sem isto, confirmar um ano já carregado não muda nada e a tela fica muda — e "não
      // aconteceu nada" é indistinguível de "falhou" para quem está olhando.
      toast.success("Nada a carregar: essas datas já estão no calendário.");
      setReview(null);
      return;
    }
    await loadHolidays.mutateAsync({ holidays: chosen });
    toast.success(
      `${added.length} ${added.length === 1 ? "feriado adicionado" : "feriados adicionados"}.`
    );
    setReview(null);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-text-tertiary" aria-hidden />
          Feriados e paradas
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {canEdit ? (
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1.5">
              <label htmlFor="holiday-year" className="text-[13px] font-medium">
                Carregar feriados nacionais
              </label>
              <div className="flex items-center gap-2">
                <Select
                  id="holiday-year"
                  className="h-9 w-[110px]"
                  value={year}
                  disabled={busy}
                  onChange={(e) => setYear(Number(e.target.value))}
                >
                  {YEARS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => setReview([])}
                >
                  <Download className="mr-1.5 h-4 w-4" aria-hidden /> Carregar {year}
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {/* A lista de revisão vem ANTES de gravar: o catálogo é sugestão, e a planta
            decide. Emendar direto no calendário economizaria um clique e tiraria a decisão. */}
        {review !== null ? (
          <HolidayReview
            year={year}
            query={holidaysQuery}
            alreadyPresent={new Set(exceptions.map((e) => e.date))}
            busy={busy}
            onCancel={() => setReview(null)}
            onConfirm={(chosen) => void confirmLoad(chosen)}
          />
        ) : null}

        {exceptions.length === 0 ? (
          <Notify.Empty
            variant="inline"
            title="Nenhum feriado cadastrado"
            description="Carregue o catálogo nacional do ano ou lance as datas à mão. Elas valem para todas as linhas que seguem o padrão da fábrica."
          />
        ) : (
          groups.map(({ year: y, items }) => (
            <details key={y} open={y === String(CURRENT_YEAR)} className="group">
              {/* Ano corrente aberto, demais recolhidos: com dez anos de cadastro sem poda
                  (research R5), abrir tudo transformaria a seção numa rolagem de centenas de
                  linhas onde o ano que importa fica no meio. */}
              <summary className="cursor-pointer list-none py-1 text-[13px] font-medium text-text-primary marker:content-none">
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-text-tertiary transition-transform group-open:rotate-90">
                    ›
                  </span>
                  {y}
                  <span className="text-[11px] font-normal text-text-tertiary">
                    ({items.length} {items.length === 1 ? "data" : "datas"})
                  </span>
                </span>
              </summary>
              <div className="pt-2">
                <ExceptionsList
                  value={items}
                  disabled={!canEdit || busy}
                  onChange={(next) => {
                    // O editor só conhece a fatia do ano; o que vai para o servidor é o conjunto
                    // inteiro com esse ano substituído. Mandar só a fatia apagaria os outros anos.
                    const outros = exceptions.filter((e) => e.date.slice(0, 4) !== y);
                    void saveExceptions([...outros, ...next]);
                  }}
                />
              </div>
            </details>
          ))
        )}
      </CardContent>
    </Card>
  );
}
